import { PdfOutputParams } from './../pdf-output-preview-dialog/pdf-output-preview-dialog-service';
import { PixoSpatial } from 'src/app/model/Pixospatial';
import { SpatialUnits } from './../../../model/Pixospatial';
import { GeoLocationQualifierService } from 'src/app/model/api-services/qualifiers/geo-location-qualifier-service';
import { SLMap, LatLonStyle } from './../../../model/sl-map';
import { SafeHtml, DomSanitizer, SafeValue, SafeStyle } from '@angular/platform-browser';
import { MapService } from 'src/app/services/map.service';
import { SubScriptionMgr } from 'src/app/model/SubscriptionMgr';
import { Component, OnInit, OnDestroy, HostBinding, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgbModalRef, NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ScaleAndLocationDialogService, ScaleAndLocationParams } from './scale-and-location-dialog-service';
import { LngLat, LngLatLike } from 'mapbox-gl';

export type PrecisionType = 'auto' | 'manual';

export type ScaleInfo = {
  length: number;
  units: SpatialUnits;
  precisionType: PrecisionType;
  precision?: number;
  pxDistance?:number;
}

@Component({
  selector: 'app-scale-and-location-dialog',
  templateUrl: './scale-and-location-dialog.html',
  styleUrls: ['./scale-and-location-dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScaleAndLocationDialogComponent implements OnInit {
  @HostBinding('class.scale-and-location-dialog') dialogStyle = true;
  @HostBinding('class.work-view') workView = true;
  public title = 'Scale and Location Display Settings';
  public get precision(): number {
    if (!this._scale?.precision) return 2;
    return this._scale.precision
  }
  public set precision(precision: number) {
    if (!this._scale) return;
    this._scale.precision = precision;
    this.Refresh();
  }

  public get precisionType(): 'auto' | 'manual' {
    if (!this._scale?.precisionType) return 'auto';
    return this._scale.precisionType;
  }
  public set precisionType(type: 'auto' | 'manual') {
    if (!this._scale) return;
    this._scale.precisionType = type;
  }

  private map?: SLMap;
  public get globalLatLonDefault(): LatLonStyle {
    if (!this.map) return 'ddeg';
    return this.map.globalLatLonDefault;
  }
  public set globalLatLonDefault(val: LatLonStyle) {
    this.map.ToggleGlobalLatLonDefault(val);
    const center: LngLat = this.map.ui.getCenter();
    this.map.src.UpdateLoc(center);
    this.Refresh();
  }

  public get globalScaleUnits(): SpatialUnits {
    if (!this.map) return 'imperial-auto';
    if (!this.map.scaleUnits) {
      this.map.scaleUnits = 'imperial-auto';
    }
    return this.map.scaleUnits;
  }
  public set globalScaleUnits(units: SpatialUnits) {
    if (!this.map) return;
    this.map.scaleUnits = units;
    this._scale.length = 0;
    this._scale.units = this.globalScaleUnits;
    this._scale.precision = this.precision;
    this._scale.precisionType = this.precisionType;
    this.Refresh();

  }
  private _scale: ScaleInfo = { length: 0, units: 'meters-auto', precisionType: 'auto', precision: 2 }
  public get scale(): number {
    if (!this.map) return 0;
    if (this._scale.length !== 0) {
      let val = Number(this._scale.length.toFixed(2));
      if(this.precisionType === 'manual') {
        return Number(val.toPrecision(this.precision));
      }
      return val;
    }
    this._scale = this.map.GetMapScale(this.globalScaleUnits);
    setTimeout(() => { this.Refresh() }, 1);
    let distance = Math.round(this._scale.length);
    if (this.precisionType === 'manual') {
      distance = Number(distance.toPrecision(this.precision));
    }
    return distance;
  }
  private _changedScale: number = 0;
  public set scale(val: number) {
    this._changedScale = val;
  }
  public get units(): string {
    return this._scale.units.split('-').pop();
  }
  set scaleUnits(u: SpatialUnits) {
    this._scale.units = u;
  }
  get scaleUnits(): SpatialUnits {
    return this._scale.units;
  }
  get unitString(): string {
    if (!this._scale?.units) return '';
    return this._scale.units.split('-').pop();
  }

  public get locationDMS(): SafeHtml {
    if (!this.map) return '';

    const center: LngLat = this.map.ui.getCenter();
    const lng = GeoLocationQualifierService.ToDegMinSec(center.lng);
    const lat = GeoLocationQualifierService.ToDegMinSec(center.lat);
    return this.sanitizer.bypassSecurityTrustHtml(`${lat}N ${lng}E`);
  }
  public get locationDD(): SafeHtml {
    if (!this.map) return '';

    const center: LngLat = this.map.ui.getCenter();
    const lng = GeoLocationQualifierService.ToDeg(center.lng);
    const lat = GeoLocationQualifierService.ToDeg(center.lat);
    return this.sanitizer.bypassSecurityTrustHtml(`${lat}N ${lng}E`);
  }

  private params?:ScaleAndLocationParams;

  private _ready = false;
  constructor(private activeModal: NgbActiveModal,
    private svc: MapService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.map = this.svc.GetMap();
    this._ready = true;
    this.Refresh();
  }

  Show(params: ScaleAndLocationParams) {
    this.params = params;
  }

  Ok() {
    const targetScale = !!this._changedScale ? this._changedScale : this._scale.length;
    const meterScale = PixoSpatial.SpatialDistanceChange(targetScale, this._scale.units, 'meters-m');
    this.map.component.SetScale(meterScale.distance)

    this.map.SetScale({ length: targetScale, units: this._scale.units, precisionType: this.precisionType, precision: this.precision });
    window.requestAnimationFrame(() => {
      this._scale.length = 0;
      this.Refresh();
      if(!!this.params?.handler) {
        this.params.handler();
      }
      this.activeModal.close();
    })
  }

  Cancel() {
    this.activeModal.close();
  }
  Refresh() {
    if (!this._ready) return;
    this.cdr.detectChanges();
  }
  HandleUnitChange() {
    this._scale.units = (this.globalScaleUnits === 'imperial-auto') ? 'imperial-ft' : 'meters-m';
  }

}

@Component({
  selector: 'app-scale-and-location-dialog-entry',
  template: ''
})
export class ScaleAndLocationDialogEntryComponent implements OnDestroy {
  modalRef: NgbModalRef;
  subscriptionMgr: SubScriptionMgr = new SubScriptionMgr();
  lastTitle: string | null = null;

  constructor(private modalService: NgbModal, private service: ScaleAndLocationDialogService) {
    this.subscriptionMgr.Subscribe(this.service.request$, (info: any) => {
      this.modalRef = this.modalService.open(ScaleAndLocationDialogComponent, { backdrop: 'static' });
      (this.modalRef.componentInstance as ScaleAndLocationDialogComponent).Show(info);
    });
  }
  ngOnDestroy(): void {
    this.subscriptionMgr.CancelSubscriptions();
  }
}
